import { useRef, useState } from "react";
import { Upload, Loader2, X, Play } from "lucide-react";
import { uploadMedia, imgUrl } from "@/lib/api";
import { toast } from "sonner";

export function MediaUploader({ media = [], onChange, label = "Photos & Videos" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const data = await uploadMedia(file);
        uploaded.push({ url: data.url, type: data.type });
      }
      onChange([...media, ...uploaded]);
      toast.success(`${uploaded.length} file(s) added`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => onChange(media.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">{label}</span>
      <div className="flex flex-wrap gap-2">
        {media.map((m, i) => (
          <div key={i} className="relative w-16 h-16 border border-white/15 bg-black overflow-hidden group">
            {m.type === "video" ? (
              <div className="w-full h-full flex items-center justify-center text-zinc-500">
                <video src={imgUrl(m.url)} className="w-full h-full object-cover" muted />
                <Play size={16} className="absolute text-white/80" />
              </div>
            ) : (
              <img src={imgUrl(m.url)} alt="" className="w-full h-full object-cover" />
            )}
            <button type="button" data-testid={`remove-media-${i}`} onClick={() => remove(i)}
              className="absolute top-0 right-0 bg-black/80 text-white hover:text-[#FF3B30] p-0.5">
              <X size={12} />
            </button>
          </div>
        ))}
        <button type="button" data-testid="upload-media-btn" onClick={() => inputRef.current?.click()} disabled={busy}
          className="w-16 h-16 border border-dashed border-white/25 flex flex-col items-center justify-center gap-1 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors text-zinc-400 disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*,video/*" multiple onChange={handleFiles} className="hidden" data-testid="media-file-input" />
    </div>
  );
}
